import { memo, useCallback, useState } from "react";
import styled from "styled-components";
import PopupWindow from "./popupWindow";

interface IProps {
  height?: string;
  width?: string;
}

const TableContainer = styled.div`
  color: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 300px;
  table {
    border-spacing: 10px;
    border: 2px solid black;
    width: 90%;
    border-collapse: collapse;
  }
  td,
  th {
    text-align: center;
    padding: 10px;
    border: 1px solid black;
    &:hover {
      cursor: default;
    }
    overflow: hidden;
    max-width: 700px;
  }
  .header {
    background-color: #d3d3d3;
  }
  .dataRow:hover {
    background-color: #f0f8ff;
  }
`;

const StyledButtonsContainer = styled.div`
  height: ${(props: IProps) => (props.height ? props.height : "100px")};
  width: ${(props: IProps) => (props.width ? props.width : "450px")};
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;
`;

const StyledButton = styled.button`
  height: 40px;
  font-size: 18px;
  width: 120px;
`;

const StyledP = styled.p`
  width: 600px;
`;

interface Props {
  data: any;
  page: number;
  handleSingleTransactionAction: (
    transactionId: string,
    action: string
  ) => void;
}

const TransactionTable: React.FC<Props> = ({
  data,
  page,
  handleSingleTransactionAction,
}): JSX.Element => {
  const [selectedTransaction, setSelectedTransaction] = useState<any>({
    transactionId: "",
    errorCode: "",
    errorMessage: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [modalType, setModalType] = useState("");

  const handleSelectTransaction = useCallback(
    (transactionId: string) => () => {
      setSelectedTransaction(
        data.find((d: any) => d.transactionId === transactionId)
      );
      setModalType("action");
      setShowModal(true);
    },
    [data]
  );

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setModalType("");
    setSelectedAction("");
    setSelectedTransaction({
      transactionId: "",
      errorCode: "",
      errorMessage: "",
    });
  }, []);

  const handleSelectedAction = useCallback(
    (action: string) => () => {
      setSelectedAction(action);
      setModalType("confirm");
    },
    []
  );

  const handleTransactionAction = useCallback(() => {
    handleModalClose();
    handleSingleTransactionAction(
      selectedTransaction.transactionId,
      selectedAction
    );
  }, [
    handleSingleTransactionAction,
    selectedTransaction.transactionId,
    selectedAction,
    handleModalClose,
  ]);

  const actionComponent = (
    <StyledButtonsContainer>
      <StyledButton onClick={handleSelectedAction("reschedule")}>
        Reschedule
      </StyledButton>
      <StyledButton
        onClick={handleSelectedAction("remove")}
        disabled={selectedTransaction.errorCode === "PAYMENT_IN_QUEUE"}
      >
        Remove
      </StyledButton>
    </StyledButtonsContainer>
  );

  const confirmComponent = (
    <>
      <StyledP>
        Do you want to {selectedAction} transactionId{" "}
        {selectedTransaction.transactionId}, error code:{" "}
        {selectedTransaction.errorCode}, error message:{" "}
        {selectedTransaction.errorMessage}
      </StyledP>
      <StyledButtonsContainer width="600px">
        <StyledButton onClick={handleTransactionAction}>Yes</StyledButton>
        <StyledButton onClick={handleModalClose}>No</StyledButton>
      </StyledButtonsContainer>
    </>
  );

  return (
    <TableContainer>
      <PopupWindow
        showModal={showModal}
        handleModalClose={handleModalClose}
        childComponent={
          modalType === "action" ? actionComponent : confirmComponent
        }
      />
      <table>
        <thead>
          <tr>
            <th className="header">#</th>
            <th className="header">TransactionId</th>
            <th className="header">ErrorCode</th>
            <th className="header">ErrorMessage</th>
            <th className="header">Client</th>
            <th className="header">ChargeType</th>
            <th className="header">FailCount</th>
          </tr>
        </thead>
        <tbody>
          {data.map((transaction: any, index: number) => {
            return (
              <tr
                key={index}
                className="dataRow"
                onClick={handleSelectTransaction(transaction.transactionId)}
              >
                <td>{50 * (page - 1) + index + 1}</td>
                <td>{transaction.transactionId}</td>
                <td>{transaction.errorCode}</td>
                <td>{transaction.errorMessage}</td>
                <td>{transaction.client}</td>
                <td>{transaction.chargeType}</td>
                <td>{transaction.failCount}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableContainer>
  );
};

export default memo(TransactionTable);
